<template>
  <LoaderComponent v-if="this.is_loading" :key="forceRerender"/>

    <h1 class="text-center">{{ betaFeatures ? "Biotron" : "Biotron Settings ⚙️" }}</h1>
    <p v-if="betaFeatures" class="biotron-subtitle text-center text-muted mb-3">
      Turn plant and light changes into music.
    </p>
    <DeviceSelector ref="biotronSelector" regex-name="Biotron" @device_changed="deviceChanged"
                    @firmware_version_changed="firmwareVersion = $event"
                    @settings_readback="settingsReadbackReceived"
                    @midi_activity="midiActivityReceived"
                    :text_label="betaFeatures ? 'Device' : '🔌 Select Device'"
                    check-versions-flag allow-daw-handoff class="mb-3"/>

    <PatchSelector :patches="this.patches" :key="this.forceRerender + this.patchRerender" :page_id="this.id"
                   :text_label="betaFeatures ? 'Sound' : '📂 Preset'"
                   :plain-language="betaFeatures" class="mb-3"/>
    <div class="row gx-2 mb-4">
      <div class="col-12 col-sm-6 col-lg-3 mb-2">
        <button @click="change_data_loader" :disabled="!this.device || this.is_loading || this.deviceMutationPending" class="btn btn-primary w-100 h-100">
          {{ betaFeatures ? "Apply settings" : "❇️ Send to Device" }}
        </button>
      </div>
      <div class="col-12 col-sm-6 col-lg-3 mb-2">
        <button @click="this.createPreset" class="btn w-100 h-100" :class="betaFeatures ? 'btn-outline-primary' : 'btn-primary'">
          {{ betaFeatures ? "Export preset" : "💾 Save Preset" }}
        </button>
      </div>
      <div class="col-12 col-sm-6 col-lg-3 mb-2">
        <UpdateFirmwareComponent class="w-100 h-100 beta-secondary-action" :text="betaFeatures ? 'Firmware' : '🔄 Update Firmware'"
                                 repo="Playtronica/biotron-firmware" :device="this.device"
                                 :current-version="firmwareVersion" :version-aware="betaFeatures"
                                 :disabled="is_loading || deviceMutationPending"/>
      </div>

      <div class="col-12 col-sm-6 col-lg-3 mb-2">
        <FileDropArea :name="betaFeatures ? 'Import preset' : '📂 Load Preset'" @get_drop="(e) => loadDataFromPreset(e)"/>
      </div>
    </div>
    <p v-if="betaFeatures && settingsStatus !== 'idle'" class="settings-status small mb-3" :class="settingsStatusClass" role="status" aria-live="polite">
      {{ settingsStatusMessage }}
    </p>
    <router-link v-if="betaFeatures" class="btn btn-link px-0 mb-3" to="/biotron/check">
      Troubleshoot
    </router-link>

  <div>
    <BootstrapCollapse :name_of_collapse="sectionTitle('plant_sound', 'PLANT SENSOR')" open_by_default>
      <template v-slot:objects>
        <GroupOfCommands>
          <template v-slot:objects>
            <div class="row m-2">
              <SwitchComponent
                  id="plantVelDis"
                  :command-label="settingLabel('plant_no_velocity', '🔇 MUTE')"
                  :description="settingDescription('plant_no_velocity', 'Turns off notes coming off plant sensor.')"
                  :show-description="false"
                  :command-object="commands_data.plant_no_velocity"
                  @input-changed="this.sys_ex_changed"
              />
            </div>

            <SliderCommand
                :command-label="settingLabel('plantBpm', '🌱 The Beat')"
                :key="this.forceRerender"
                :command-object="this.commands_data.plantBpm"
                :description="settingDescription('plantBpm', 'Set tempo of plant notes, plant’s BPM.')"
                :show-description="false"
                @input-changed="this.sys_ex_changed"
                class="m-2"
            />

            <SliderCommand
                :command-label="settingLabel('noteOffPercent', '🎵 Note Hold')"
                :key="this.forceRerender"
                :command-object="this.commands_data.noteOffPercent"
                :table-values="this.fractions_note_off"
                :description="settingDescription('noteOffPercent', 'How long a note plays before stopping.')"
                :show-description="false"
                @input-changed="this.sys_ex_changed"
                table-values-reversed
                class="m-2"
            />

            <SliderCommand
                :command-label="settingLabel('middle_plant_note', '🏠︎ Home Note')"
                :key="this.forceRerender"
                :command-object="this.commands_data.middle_plant_note"
                :table-values="this.root_note_id"
                :description="settingDescription('middle_plant_note', 'The main note everything starts from and returns to.')"
                :show-description="false"
                @input-changed="this.sys_ex_changed"
                class="m-2"
            />

            <SelectCommand
                :command-label="settingLabel('scale', '🎼 Scale')"
                :key="this.forceRerender"
                :list-of-variants="betaFeatures ? plainScales : scales"
                :command-object="commands_data.scale"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('scale', 'A set of notes that shape the melody and feel of the music.')"
                :show-description="false"
                class="m-3"
            />

          </template>
        </GroupOfCommands>
      </template>
    </BootstrapCollapse>

  </div>

  <div>
    <BootstrapCollapse :name_of_collapse="sectionTitle('plant_response', 'MORE FUN')">
      <template v-slot:objects>
        <GroupOfCommands :name-of-group="betaFeatures ? 'Where plant notes go' : 'Plant Midi Channel'">
          <template v-slot:objects>
            <SliderCommand
                :command-label="settingLabel('plant_midi_channel', '🎛️ MIDI channel')"
                :description="settingDescription('plant_midi_channel', 'Pick a MIDI channel for the plant.')"
                :show-description="false"
                :key="this.forceRerender"
                :command-object="this.commands_data.plant_midi_channel"
                @input-changed="this.sys_ex_changed"
                class="m-2"
            />
          </template>
        </GroupOfCommands>

        <GroupOfCommands :name-of-group="betaFeatures ? 'Mute pad' : 'Buttons Mode'">
          <template v-slot:objects>
            <SwitchComponent
                id="randomPlantVelSwitch"
                :command-label="settingLabel('button_mode_state', 'Mute button state')"
                :description="settingDescription('button_mode_state', 'Enables and disables mute button.')"
                :show-description="false"
                :command-object="this.commands_data.button_mode_state"
                @input-changed="this.sys_ex_changed"

            />
          </template>
        </GroupOfCommands>

        <GroupOfCommands :name-of-group="betaFeatures ? 'Rhythm feel' : 'Swing note'">
          <template v-slot:objects>
            <SliderCommand
                :command-label="settingLabel('swing_first_note_percent', 'Swing note')"
                :description="settingDescription('swing_first_note_percent', 'Duration of the first note compared with the beat.')"
                :show-description="false"
                :key="this.forceRerender"
                :command-object="this.commands_data.swing_first_note_percent"
                @input-changed="this.sys_ex_changed"
                class="m-2"
            />
          </template>
        </GroupOfCommands>

        <GroupOfCommands :name-of-group="betaFeatures ? 'Plant note strength' : 'NOTE VELOCITY'">
          <template v-slot:objects>
            <div class="row m-2">
              <div class="col">
                <SwitchComponent
                    id="randomPlantVelSwitch"
                    :command-label="settingLabel('randomPlantVelocity', '🧍Humanize')"
                    :description="settingDescription('randomPlantVelocity', 'Adds natural variations to note strength.')"
                    :show-description="false"
                    :command-object="this.commands_data.randomPlantVelocity"
                    @input-changed="this.sys_ex_changed"

                />
              </div>
            </div>

            <div v-if="!commands_data.plant_no_velocity.value">
              <div v-if="!this.commands_data.randomPlantVelocity.value">
                <SliderCommand :key="this.forceRerender"
                               :command-object="commands_data.maxPlantVelocity"
                               @input-changed="this.sys_ex_changed"
                               :command-label="settingLabel('maxPlantVelocity', '💪 Note velocity')"
                               :description="settingDescription('maxPlantVelocity', 'Intensity of notes (volume or expression).')"
                               :show-description="false"
                               class="m-2"
                />
              </div>
              <div v-else>
                <SliderRangeCommand :key="this.forceRerender"
                                    :max-command-object="commands_data.maxPlantVelocity"
                                    :min-command-object="commands_data.minPlantVelocity"
                                    @input-changed="this.sys_ex_changed"
                                    :command-label="settingLabel('minPlantVelocity', '💪 Note velocity')"
                                    :description="settingDescription('minPlantVelocity', 'Intensity range of notes (volume or expression).')"
                                    :show-description="false"
                                    class="m-2"
                />
              </div>
            </div>
          </template>
        </GroupOfCommands>
        <GroupOfCommands :name-of-group="betaFeatures ? 'Plant response and melody movement' : 'SENSITIVITY'">
          <template v-slot:objects>
            <div class="row m-2">
              <div class="col">
                <SwitchComponent
                    :key="this.forceRerender"
                    :command-label="settingLabel('randomness', '📡 Ultra sensitivity')"
                    :command-object="commands_data.randomness"
                    @input-changed="this.sys_ex_changed"
                    :description="settingDescription('randomness', 'Makes the plant more responsive.')"
                    :show-description="false"
                />
              </div>
              <div class="col">
                <SwitchComponent
                    :key="this.forceRerender"
                    :command-label="settingLabel('performance', '✋ Manual control')"
                    :command-object="commands_data.performance"
                    @input-changed="this.sys_ex_changed"
                    :description="settingDescription('performance', 'Plant only reacts to human interaction and does not play by itself.')"
                    :show-description="false"
                    :invert-value="betaFeatures"
                    :checked-text="betaFeatures ? 'Living melody on' : ''"
                    :unchecked-text="betaFeatures ? 'Plant signal only' : ''"
                />
              </div>
            </div>

            <SliderCommand
                :key="this.forceRerender"
                :command-object="commands_data.same_note_plant"
                :command-label="settingLabel('same_note_plant', '🔂 Note Repeat')"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('same_note_plant', 'Move near the plant to change notes.')"
                :show-description="false"
                class="m-2"
            />
            <SliderCommand
                :command-label="settingLabel('firstValue', '🌞 Wake-Up')"
                :key="this.forceRerender"
                :command-object="this.commands_data.firstValue"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('firstValue', 'A little change that wakes up the first note.')"
                :show-description="false"
                class="m-2"
            />
            <SliderCommand
                :command-label="settingLabel('noteDistance', '👣 Step Size')"
                :key="this.forceRerender"
                :command-object="this.commands_data.noteDistance"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('noteDistance', 'How big a change is needed to go to the next note.')"
                :show-description="false"
                class="m-2"
            />
            <SliderCommand
                :command-label="settingLabel('smoothness', '⏳ Delay')"
                :key="this.forceRerender"
                :command-object="this.commands_data.smoothness"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('smoothness', 'How quickly the device reacts to change.')"
                :show-description="false"
                class="m-2"
            />
          </template>
        </GroupOfCommands>
      </template>
    </BootstrapCollapse>
  </div>

  <div>
    <BootstrapCollapse :name_of_collapse="sectionTitle('light_sound', 'LIGHT SENSOR')">
      <template v-slot:objects>
        <GroupOfCommands>
          <template v-slot:objects>
            <div class="row m-2">
              <div class="col">
                <SwitchComponent
                    id="lightVelDis"
                    :command-label="settingLabel('light_no_velocity', '🔇Mute')"
                    :command-object="commands_data.light_no_velocity"
                    @input-changed="this.sys_ex_changed"
                    :description="settingDescription('light_no_velocity', 'Turns off notes coming from the light sensor.')"
                    :show-description="false"
                />
              </div>
              <div class="col">
                <SwitchComponent
                    id="randomLightVelSwitch"
                    :command-label="settingLabel('randomLightVelocity', '🧍Humanize')"
                    :command-object="this.commands_data.randomLightVelocity"
                    @input-changed="this.sys_ex_changed"
                    :description="settingDescription('randomLightVelocity', 'Adds natural variations to note strength.')"
                    :show-description="false"
                />
              </div>
              <div class="col">
                <SwitchComponent
                    id="light_pitch_mode"
                    :command-label="settingLabel('light_pitch_mode', '〜 Pitch Bend')"
                    :command-object="this.commands_data.light_pitch_mode"
                    @input-changed="this.sys_ex_changed"
                    :description="settingDescription('light_pitch_mode', 'Uses the light like a pitch wheel on a keyboard.')"
                    :show-description="false"
                />
              </div>
            </div>

            <SliderCommand
                :command-label="settingLabel('light_midi_channel', '🎛️ MIDI channel')"
                :description="settingDescription('light_midi_channel', 'Pick a MIDI channel for the light sensor.')"
                :show-description="false"
                :key="this.forceRerender"
                :command-object="this.commands_data.light_midi_channel"
                @input-changed="this.sys_ex_changed"
                class="m-2"
            />

            <SliderCommand
                :command-label="settingLabel('lightBpm', '🌞 The Beat')"
                :key="this.forceRerender"
                :command-object="this.commands_data.lightBpm"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('lightBpm', 'Set how often light sensor notes play.')"
                :show-description="false"
                class="m-2"
            />

            <SliderCommand
                :key="this.forceRerender"
                :command-object="commands_data.same_note_light"
                :command-label="settingLabel('same_note_light', '🔂Note Repeat')"
                @input-changed="this.sys_ex_changed"
                :description="settingDescription('same_note_light', 'Change the light to change notes.')"
                :show-description="false"
                class="m-2"
            />

            <div class="row" v-if="!commands_data.light_no_velocity.value">
              <div v-if="!this.commands_data.randomLightVelocity.value">
                <SliderCommand
                    :key="this.forceRerender"
                    :command-object="commands_data.maxLightVelocity"
                    @input-changed="this.sys_ex_changed"
                    :name="commands_data.maxLightVelocity.value"
                    :command-label="settingLabel('maxLightVelocity', '🔨 Note velocity')"
                    :description="settingDescription('maxLightVelocity', 'Intensity of notes (volume or expression).')"
                    :show-description="false"
                    class="m-2"
                />
              </div>
              <div v-else>
                <SliderRangeCommand
                    :key="this.forceRerender"
                    :max-command-object="commands_data.maxLightVelocity"
                    :min-command-object="commands_data.minLightVelocity"
                    @input-changed="this.sys_ex_changed"
                    :command-label="settingLabel('minLightVelocity', '🔨 Note velocity')"
                    :description="settingDescription('minLightVelocity', 'Intensity range of notes (volume or expression).')"
                    :show-description="false"
                    class="m-2"
                />
              </div>
            </div>

            <SliderCommand
                v-if="!this.commands_data.light_pitch_mode.value"
                :key="this.forceRerender"
                :command-object="this.commands_data.range_light_note"
                @input-changed="this.sys_ex_changed"
                :command-label="settingLabel('range_light_note', '📏 Range')"
                :description="settingDescription('range_light_note', 'How many notes are played relative to the starting note.')"
                :show-description="false"
                class="m-2"
            />

          </template>
        </GroupOfCommands>
      </template>
    </BootstrapCollapse>

  </div>
</template>

<script>

import { sleep } from "@/assets/js/SysExCommand"
import { saveAs } from '@progress/kendo-file-saver';
import {BiotronCommandsData, BiotronDb} from "@biotron-idb"
import FileDropArea from "@/components/MidiComponents/FileDropArea.vue";
import GroupOfCommands from "@/components/MidiComponents/GroupOfCommands.vue";
import SwitchComponent from "@/components/MidiComponents/Switch.vue";
import SliderCommand from "@/components/MidiComponents/SliderCommand.vue";
import SliderRangeCommand from "@/components/MidiComponents/SliderRangeCommand.vue";
import SelectCommand from "@/components/MidiComponents/SelectCommand.vue";
import PatchSelector from "@/components/MidiComponents/PatchSelector.vue";
import DeviceSelector from "@biotron-device-selector";
import UpdateFirmwareComponent from "@/components/MidiComponents/UpdateFirmwareComponent.vue";
import LoaderComponent from "@/components/MidiComponents/LoaderComponent.vue";
import BootstrapCollapse from "@/components/BootstrapCollapse.vue";
import {BIOTRON_DIAGNOSTICS_LOCK_NAME} from "@/assets/js/BiotronSafeInspector";
import BiotronPlainLanguage from "@/assets/data/biotron-plain-language.json";
import {
  BIOTRON_SETTINGS_QUERY_ID,
  biotronCommandsFromSettingsReply,
  biotronSettingsAreSaved,
  biotronSettingsVector
} from "@biotron-settings-readback";

const delay = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))


export default  {
  components: {
    BootstrapCollapse,
    LoaderComponent,
    UpdateFirmwareComponent,
    DeviceSelector,
    PatchSelector,
    SelectCommand,
    SliderRangeCommand,
    SliderCommand,
    SwitchComponent,
    GroupOfCommands,
    FileDropArea},
  props: {
    id: {
      type: String,
      required: true,
    },
    test: {
      type: Boolean,
      default: false
    },
    betaFeatures: {
      type: Boolean,
      default: false
    },
  },
  computed: {
    settingsStatusMessage() {
      const messages = {
        idle: "",
        loading: "Reading saved settings…",
        loaded: "Settings loaded from Biotron.",
        ready: "Connected. Changes are sent immediately.",
        changed: "Not sent yet. Connect Biotron or press Apply settings.",
        sending: "Applying settings…",
        sent: "Sent, but saving could not be confirmed.",
        saved: "Saved in Biotron.",
        mismatch: "Values differ. Reconnect before continuing.",
        error: "Could not send. Close other MIDI apps, reconnect and retry."
      }
      return messages[this.settingsStatus]
    },
    settingsStatusClass() {
      if (this.settingsStatus === "error" || this.settingsStatus === "mismatch") return "text-danger"
      if (this.settingsStatus === "saved") return "text-success"
      if (this.settingsStatus === "sent") return "text-primary"
      if (this.settingsStatus === "sending") return "text-warning"
      return "text-muted"
    }
  },
  methods: {
    settingLabel(name, legacy) {
      return this.betaFeatures && BiotronPlainLanguage.settings[name]
        ? BiotronPlainLanguage.settings[name].label : legacy
    },
    settingDescription(name, legacy) {
      return this.betaFeatures && BiotronPlainLanguage.settings[name]
        ? BiotronPlainLanguage.settings[name].description : legacy
    },
    sectionTitle(name, legacy) {
      return this.betaFeatures && BiotronPlainLanguage.sections[name]
        ? BiotronPlainLanguage.sections[name].title : legacy
    },
    async withDeviceMutation(operation) {
      if (!this.betaFeatures || !navigator.locks || typeof navigator.locks.request !== "function") {
        return operation()
      }
      if (this.deviceMutationPending) return false
      this.deviceMutationPending = true
      let completed = false
      try {
        await navigator.locks.request(BIOTRON_DIAGNOSTICS_LOCK_NAME, {ifAvailable: true}, async lock => {
          if (!lock) {
            this.settingsStatus = "error"
            return
          }
          await operation()
          completed = true
        })
      } finally {
        this.deviceMutationPending = false
      }
      return completed
    },
    deviceChanged(device) {
      this.deviceEpoch++
      this.lastMidiEvent = null
      this.device = device
      if (!this.betaFeatures) return
      this.sendOperationId++
      this.firmwareVersion = null
      this.settingsReadback = null
      this.is_loading = false
      this.settingsStatus = device ? "loading" : "idle"
      if (device) this.loadSettingsFromDevice(device, this.sendOperationId)
    },
    isCurrentSend(device, operationId) {
      return this.device === device && this.sendOperationId === operationId
    },
    async change_data_loader() {
      if (!this.betaFeatures) {
        this.changeDataLegacy()
        return
      }
      if (!this.device || this.is_loading || this.deviceMutationPending) return
      return this.withDeviceMutation(() => this.changeDataBeta())
    },
    async changeDataBeta() {
      const device = this.device
      const operationId = ++this.sendOperationId
      const expectedValues = biotronSettingsVector(this.commands_data)
      this.is_loading = true
      this.settingsStatus = "sending"
      this.forceRerender++
      try {
        await delay(100)
        if (!await this.sendSettings(device, operationId, false)) return
        await delay(100)
        if (await this.sendSettings(device, operationId, true) && this.isCurrentSend(device, operationId)) {
          const proof = await this.verifySettingsSaved(device, operationId, expectedValues)
          if (!this.isCurrentSend(device, operationId)) return
          this.settingsStatus = proof === "saved" ? "saved" :
            proof === "mismatch" ? "mismatch" : "sent"
        }
      } catch (error) {
        if (this.isCurrentSend(device, operationId)) this.settingsStatus = "error"
        console.log("Could not send Biotron settings", error)
      } finally {
        if (operationId === this.sendOperationId) {
          this.is_loading = false
          this.forceRerender++
        }
      }
    },
    changeDataLegacy() {
      if (!this.device) return
      sleep(100)
      this.is_loading = true
      this.forceRerender++
      setTimeout(() => {
        this.is_loading = false
        this.forceRerender++
      }, 3000)
      setTimeout(() => {
        const protocols = [
          {flag: undefined, start: [240, 11, 20, 13, 126, 247], query: [240, 11, 20, 13, 126, 247], initialDelay: true},
          {flag: [11], start: [240, 11, 16, 127, 247], query: [240, 11, 126, 247], initialDelay: false}
        ]
        for (const [index, protocol] of protocols.entries()) {
          if (!this.device) return
          this.device.send(protocol.start)
          if (protocol.initialDelay) sleep(100)
          for (const [name, command] of Object.entries(this.commands_data)) {
            if (name === "plantBpm") continue
            command.sendToMidi(this.device, protocol.flag)
            sleep(100)
          }
          if (protocol.initialDelay) sleep(100)
          this.device.send(protocol.query)
          sleep(100)
          this.commands_data.plantBpm.sendToMidi(this.device, protocol.flag)
          if (index === 0) sleep(100)
        }
      }, 10)
    },
    async sendSettings(device, operationId, legacy) {
      if (!this.isCurrentSend(device, operationId)) return false
      const query = legacy ? [240, 11, 126, 247] : [240, 11, 20, 13, 126, 247]
      const initial = legacy ? [240, 11, 16, 127, 247] : query
      const sendCommand = command => legacy
        ? command.sendToMidi(device, [11])
        : command.sendToMidi(device)

      device.send(initial)
      for (const [name, command] of Object.entries(this.commands_data)) {
        if (name === "plantBpm") continue
        await delay(100)
        if (!this.isCurrentSend(device, operationId)) return false
        sendCommand(command)
      }
      await delay(100)
      if (!this.isCurrentSend(device, operationId)) return false
      device.send(query)
      await delay(100)
      if (!this.isCurrentSend(device, operationId)) return false
      sendCommand(this.commands_data.plantBpm)
      return true
    },
    settingsReadbackReceived(reply) {
      if (this.betaFeatures && this.device) this.settingsReadback = reply
    },
    midiActivityReceived(event) {
      if (this.betaFeatures) this.lastMidiEvent = event
    },
    async runSafeInspection(request) {
      if (!this.betaFeatures || !this.device) return
      this.safeInspectionRunning = true
      this.safeInspectionResult = null
      try {
        const selector = this.$refs.biotronSelector
        this.safeInspectionResult = selector && typeof selector.inspectAllPortsReadOnly === "function"
          ? await selector.inspectAllPortsReadOnly(request)
          : {...request, status: "blocked", reason: "inspector_transport_unavailable", ports: []}
      } catch (error) {
        this.safeInspectionResult = {...request, status: "fail", reason: "unexpected_inspector_error", ports: []}
        console.log("Safe Inspector failed", error)
      } finally {
        this.safeInspectionRunning = false
      }
    },
    async loadSettingsFromDevice(device, operationId) {
      this.is_loading = true
      try {
        for (let attempt = 0; attempt < 3; attempt++) {
          if (!this.isCurrentSend(device, operationId)) return
          this.settingsReadback = null
          const requestId = ++this.settingsReadbackRequestId & 0x7f
          device.send([0xf0, 0x14, 0x0d, BIOTRON_SETTINGS_QUERY_ID, 1, requestId, 0xf7])
          for (let poll = 0; poll < 5; poll++) {
            await delay(100)
            if (!this.isCurrentSend(device, operationId)) return
            if (this.settingsReadback && this.settingsReadback.requestId === requestId) break
          }
          const reply = this.settingsReadback && this.settingsReadback.requestId === requestId
            ? this.settingsReadback : null
          const values = biotronCommandsFromSettingsReply(reply)
          if (!values) continue
          if (Object.entries(values).some(([name, value]) => {
            const command = this.commands_data[name]
            return !command || value < command.min_value || value > command.max_value
          })) {
            this.settingsStatus = "error"
            return
          }
          for (const [name, value] of Object.entries(values)) this.commands_data[name].set_value(value)
          this.forceRerender++
          this.patchRerender++
          this.settingsStatus = "loaded"
          return
        }
        this.settingsStatus = "ready"
      } catch (error) {
        if (this.isCurrentSend(device, operationId)) this.settingsStatus = "ready"
        console.log("Could not read Biotron settings", error)
      } finally {
        if (this.isCurrentSend(device, operationId)) this.is_loading = false
      }
    },
    async verifySettingsSaved(device, operationId, expectedValues) {
      if (!this.betaFeatures) return "unavailable"
      let receivedReply = false
      for (let attempt = 0; attempt < 3; attempt++) {
        await delay(attempt === 0 ? 1100 : 400)
        if (!this.isCurrentSend(device, operationId)) return "cancelled"
        this.settingsReadback = null
        const requestId = ++this.settingsReadbackRequestId & 0x7f
        device.send([0xf0, 0x14, 0x0d, BIOTRON_SETTINGS_QUERY_ID, 1, requestId, 0xf7])
        for (let poll = 0; poll < 5; poll++) {
          await delay(100)
          if (!this.isCurrentSend(device, operationId)) return "cancelled"
          if (this.settingsReadback && this.settingsReadback.requestId === requestId) break
        }
        const reply = this.settingsReadback && this.settingsReadback.requestId === requestId
          ? this.settingsReadback : null
        if (!reply) continue
        receivedReply = true
        if (biotronSettingsAreSaved(reply, expectedValues)) return "saved"
        if (reply.source === 1 && reply.sourceValid && !reply.dirty) return "mismatch"
      }
      return receivedReply ? "mismatch" : "unavailable"
    },

    saveData() {
      let state = {}
      for (let val of Object.values(this.commands_data)) {
        state[val.name] = val.value
      }

      this.db.updatePatch(localStorage.getItem(this.id), state)
    },

    async loadData() {
      let preset = await this.db.getPatch(localStorage.getItem(this.id))
      if (!preset) {
        localStorage.setItem(this.id, 1)
        preset = await this.db.getPatch(localStorage.getItem(this.id))
      }

      for (const [key, value] of Object.entries(preset.data)) {
        this.commands_data[key].set_value(value);
      }

      this.forceRerender++;
    },
    createPreset() {
      let state = []
      for (let item in this.commands_data) {
        state.push(this.commands_data[item].toShortDict())
      }

      let value = {"commands": state}
      let myFile = new File([JSON.stringify(value)], "biotron-preset.txt",
          {type: "text/plain;charset=utf-8"})
      saveAs(myFile, "biotron-preset.txt");
    },
    async loadDataFromPreset(e) {
      await this.patchChanged();
      for (let item of JSON.parse(e).commands) {
        this.commands_data[item.name].set_value(item.value);
      }
      this.saveData();
      if (this.betaFeatures) {
        this.settingsStatus = "changed"
      }
      this.forceRerender++;
    },
    async patchChanged() {
      let patch_id = parseInt(localStorage.getItem(this.id));

      if (this.patches.find(item => item.id === patch_id).saved) {
        patch_id = await this.db.getUnsavedPatch();
        this.patches = await this.db.getPatch();
        localStorage.setItem(this.id, patch_id);
      }
      this.saveData();
    },
    async sys_ex_changed(object) {
      if (this.is_loading) return
      await this.patchChanged();
      if (!this.betaFeatures) {
        if (this.device) await object.sendToMidi(this.device)
        this.forceRerender++
        this.patchRerender++
        return
      }
      return this.withDeviceMutation(() => this.sendBetaSetting(object))
    },
    async sendBetaSetting(object) {
      if (!this.device) {
        this.settingsStatus = "changed"
      } else {
        const device = this.device
        const operationId = this.sendOperationId
        this.settingsStatus = "sending"
        try {
          await object.sendToMidi(device)
          if (this.isCurrentSend(device, operationId)) this.settingsStatus = "sent"
        } catch (error) {
          if (this.isCurrentSend(device, operationId)) this.settingsStatus = "error"
          console.log("Could not send Biotron setting", error)
        }
      }
      this.forceRerender++;
      this.patchRerender++;
    },
    async handlePatchChanged() {
      await this.loadData()
      if (this.betaFeatures) this.settingsStatus = "changed"
      this.forceRerender++
    },
    async handlePatchSave(event) {
      this.db.savePatch(localStorage.getItem(this.id), event.detail)
      this.patches = await this.db.getPatch()
      this.patchRerender++
    },
    async handlePatchDelete() {
      this.db.deletePatch(parseInt(localStorage.getItem(this.id)))
      localStorage.setItem(this.id, "1")
      this.patches = await this.db.getPatch()
      await this.loadData()
      if (this.betaFeatures) this.settingsStatus = "changed"
      this.forceRerender++
    },
    handleKeyup(event) {
      if (!this.betaFeatures && event.code === 'Enter' && !this.is_loading) this.change_data_loader()
    },
  },
  data() {
    return {
      page_is_inited: false,
      scales: ["Major", "Minor", "Chrom", "Dorian", "Mixolydian",
        "Lydian", "Wholetone", "Minblues", "Majblues", "Minpen",
        "Majpen", "Diminished", "Hirajōshi"],
      plainScales: ["Major", "Minor", "Chromatic", "Dorian", "Mixolydian",
        "Lydian", "Whole-tone", "Minor blues", "Major blues", "Minor pentatonic",
        "Major pentatonic", "Diminished", "Hirajōshi"],
      root_note_id: {
        60: 'C4', 61: 'C#4', 62: 'D4', 63: 'D#4', 64: 'E4', 65: 'F4',
        66: 'F#4', 67: 'G4', 68: 'G#4', 69: 'A4', 70: 'A#4', 71: 'B4', 72: 'C5',
      },
      fractions_note_off: {
        64: "1/64", 48: "1/48", 32: "1/32", 24: "1/24", 16: "1/16", 12: "1/12",
        8: "1/8", 6: "1/6", 4: "1/4", 2: "1/2", 1: "1"
      },
      device: null,
      forceRerender: 0,
      patchRerender: 0,
      db: {
        type: BiotronDb
      },
      patches: [],
      patch_id: 0,
      is_loading: false,
      deviceMutationPending: false,
      settingsStatus: "idle",
      firmwareVersion: null,
      settingsReadback: null,
      settingsReadbackRequestId: 0,
      sendOperationId: 0,
      deviceEpoch: 0,
      lastMidiEvent: null,
      buildId: process.env.VUE_APP_BUILD_ID || "local-build",
      commands_data: Object.fromEntries(BiotronCommandsData),
    }
  },
  async created() {
    if (!localStorage.getItem(this.id)) {
      localStorage.setItem(this.id, "1")
    }

    this.db = new BiotronDb();
    await this.db.openDB();

    this.patches = await this.db.getPatch();
    this.patch_id = parseInt(localStorage.getItem(this.id));

    await this.loadData()
    this.forceRerender++;
    this.page_is_inited = true

  },
  mounted() {
    document.addEventListener('keyup', this.handleKeyup)
    document.addEventListener('PatchChanged', this.handlePatchChanged)
    document.addEventListener('PatchSave', this.handlePatchSave)
    document.addEventListener('PatchDelete', this.handlePatchDelete)
  },
  beforeUnmount() {
    document.removeEventListener('keyup', this.handleKeyup)
    document.removeEventListener('PatchChanged', this.handlePatchChanged)
    document.removeEventListener('PatchSave', this.handlePatchSave)
    document.removeEventListener('PatchDelete', this.handlePatchDelete)
  }

}
</script>

<style scoped>
.biotron-subtitle { font-size: 1rem; }
.settings-status { min-height: 1.25rem; }
:deep(.beta-secondary-action) {
  color: #0d6efd;
  background: #fff;
  border-color: #0d6efd;
}
:deep(.beta-secondary-action:hover),
:deep(.beta-secondary-action:focus) {
  color: #fff;
  background: #0d6efd;
}
</style>
